import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { competitionService } from '../services/competition';
import { createBettingApi } from '../services/bettingApi';
import { useAuth } from './AuthContext';
import { useMasterEvent } from './MasterEventContext';

const BettingContext = createContext(null);
const defaults = { events: [], rounds: [], markets: [], bets: [], drafts: [], standings: [], members: [], reactions: [], settings: { allowBetReplacement: true } };

export function BettingProvider({ children }) {
  const { id: masterEventId, actId: activityId } = useParams();
  const { user } = useAuth();
  const { refreshData: refreshEvent } = useMasterEvent();
  const [state, setState] = useState(defaults);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const api = useMemo(() => createBettingApi(masterEventId, activityId), [masterEventId, activityId]);
  const loadData = useCallback(async () => {
    try {
      const result = await competitionService.load(masterEventId, activityId);
      setState(result.betting); setError('');
    } catch (err) { setError(err.message || 'Could not load betting'); }
    finally { setLoading(false); }
  }, [masterEventId, activityId]);
  useEffect(() => {
    loadData();
    const timer = setInterval(loadData, 10000);
    window.addEventListener('focus', loadData);
    return () => { clearInterval(timer); window.removeEventListener('focus', loadData); };
  }, [loadData]);
  const command = async (action, payload) => {
    const result = await competitionService.command(masterEventId, activityId, action, payload);
    setState(result.betting); setError('');
    return result;
  };
  const isMyBet = bet => state.participant?.teamId ? bet.teamId === state.participant.teamId : !bet.teamId && bet.participantId === state.participant?.id;
  const myBets = state.bets.filter(isMyBet);
  const pending = myBets.filter(b => b.status === 'pending');
  const livePotential = {
    activeBets: pending.length,
    totalStaked: pending.reduce((sum, b) => sum + b.amount, 0),
    totalPotentialWin: pending.reduce((sum, b) => sum + b.potentialWin, 0),
    potentialProfit: pending.reduce((sum, b) => sum + b.potentialWin - b.amount, 0),
    avgOdds: pending.length ? pending.reduce((sum, b) => sum + b.odds, 0) / pending.length : 0,
  };
  const value = {
    ...state, api, loading, error, livePotential, isMyBet, myBets,
    bettingUser: { ...user, balance: state.balance || 0 },
    placeBet: (marketId, optionName, amount) => {
      if (state.isTeamBased && !state.participant) throw new Error('Ask an event admin to assign you to a team before betting. Personal balances cannot be used in a team activity.');
      return command('placeBet', { marketId, optionName, amount });
    },
    replaceBet: (betId, optionName, amount) => command('replaceBet', { betId, optionName, amount }),
    cancelBet: betId => command('cancelBet', { betId }),
    saveDraft: payload => command('saveDraft', payload),
    addReaction: (betId, emoji) => command('addReaction', { betId, emoji }),
    addBetComment: (betId, text) => command('addComment', { betId, text }),
    submitDraft: payload => command('submitDraft', payload),
    closeMarket: marketId => command('closeMarket', { marketId }),
    reopenMarket: marketId => command('reopenMarket', { marketId }),
    updateSettings: payload => command('updateSettings', payload),
    refreshData: async () => { await loadData(); await refreshEvent(); },
  };
  return <BettingContext.Provider value={value}>
    {error && <div role="alert" className="mea-error">{error} <button onClick={loadData}>Retry</button></div>}
    {!loading && !error && children}
    {loading && <p>Loading betting activity...</p>}
  </BettingContext.Provider>;
}
export function useBetting() {
  const context = useContext(BettingContext);
  if (!context) throw new Error('useBetting must be used within BettingProvider');
  return context;
}
